/**
 * Iter XX.12 — Cricket Journey Started celebration.
 * Iter XX.93 — Shortened to a true transition, not a loading gate.
 *
 * Diagnosis (from founder brief): the overlay was staying for 4.9s
 * because two setTimeout constants (this file's t3=4900ms and the
 * matched Chat.jsx welcome-message scheduler) hardcoded it that way.
 * There was NEVER a network dependency: the chat is already mounted
 * behind this overlay before it renders, and /api/web/identity has
 * already resolved before the parent even sets visible=true.
 *
 * Fix: keep this component a pure visual transition. Total budget
 * ~700ms (fade-in 120ms + hold 380ms + fade-out 200ms), well under
 * the <800ms founder target. Any timing telemetry is emitted by the
 * parent (Chat.jsx) so this component stays render-only.
 */
import React, { useEffect, useState } from "react";

// Iter XX.93 — Timing constants. Deliberately module-level so tests
// and telemetry can import them; changes are one-line for the whole
// transition, not scattered across setTimeout call sites.
export const JOURNEY_STARTED_ENTER_MS = 0;
export const JOURNEY_STARTED_HOLD_AT_MS = 120;   // fade-in completes
export const JOURNEY_STARTED_EXIT_AT_MS = 500;   // hold complete, fade-out begins
export const JOURNEY_STARTED_GONE_AT_MS = 700;   // fade-out complete → dismiss
export const JOURNEY_STARTED_TOTAL_MS = JOURNEY_STARTED_GONE_AT_MS;

export default function CricketJourneyStarted({ visible, onFinished }) {
  const [phase, setPhase] = useState("enter"); // enter | hold | exit | gone

  useEffect(() => {
    if (!visible) { setPhase("gone"); return; }
    setPhase("enter");
    const t1 = setTimeout(() => setPhase("hold"), JOURNEY_STARTED_HOLD_AT_MS);
    const t2 = setTimeout(() => setPhase("exit"), JOURNEY_STARTED_EXIT_AT_MS);
    const t3 = setTimeout(
      () => { setPhase("gone"); onFinished?.(); },
      JOURNEY_STARTED_GONE_AT_MS,
    );
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [visible, onFinished]);

  if (!visible || phase === "gone") return null;

  const fade = phase === "exit" ? "opacity-0" : "opacity-100";
  const scale = phase === "enter" ? "scale-95" : "scale-100";

  return (
    <div
      className={`fixed inset-0 z-50 bg-gradient-to-b from-emerald-950 via-neutral-950 to-black flex items-center justify-center px-4 transition-opacity duration-200 ${fade}`}
      data-testid="cricket-journey-started"
      aria-live="polite"
    >
      <div className={`text-center transition-transform duration-200 ${scale}`}>
        <div className="text-6xl sm:text-7xl mb-4" aria-hidden="true">🏏</div>
        <h2
          className="text-2xl sm:text-3xl font-medium text-emerald-200 mb-3"
          data-testid="cricket-journey-started-title"
        >
          Cricket Journey Started
        </h2>
        <p className="max-w-md mx-auto text-sm sm:text-base text-neutral-300 leading-relaxed">
          From this moment on, Gully will remember your matches,
          your videos, your patterns and your progress.
        </p>
      </div>
    </div>
  );
}
